import React, { useEffect, useMemo } from 'react'
import { setInitialActionPerformed, useChoiceDispatch, useChoiceState } from '@context/ChoiceContext'
import { useConfig } from '@context/ConfigurationContext'
import { TreatmentScreen } from '@screens/TreatmentScreen/TreatmentScreen'
import { useSelectTreatmentForManyClinics } from '@hooks/useSelectTreatmentForManyClinics'
import {
  CategorizedAndUniqueTreatments,
  CategorizedTreatmentsWithPartialTreatments,
} from '@customTypes/CategorizedTreatment'
import { useIsSuggestedTreatment } from '@hooks/useIsSuggestedTreatment'
import { getSuggestedTreatmentsCategory } from 'src/services/api/treatment'

interface Props {
  categorizedAndUniqueTreatments: CategorizedAndUniqueTreatments | undefined
  isLoading: boolean
  variantTreatmentBundleTypeReferenceId: string | undefined
  goToNextPage: () => void
}

export const ManyClinicsTreatment = ({
  categorizedAndUniqueTreatments,
  isLoading,
  variantTreatmentBundleTypeReferenceId,
  goToNextPage,
}: Props) => {
  const choiceDispatch = useChoiceDispatch()
  const { clinicId } = useConfig()
  const { initialState, treatmentBundleTypeReferenceId } = useChoiceState()
  const { uniqueCategorizedTreatments, categorizedTreatments } = categorizedAndUniqueTreatments || {}

  const handleSelectTreatment = useSelectTreatmentForManyClinics(goToNextPage, categorizedTreatments || [])

  useEffect(() => {
    if (variantTreatmentBundleTypeReferenceId && !initialState.initialActionPerformed) {
      handleSelectTreatment(variantTreatmentBundleTypeReferenceId)
      choiceDispatch(setInitialActionPerformed())
    }
  }, [
    initialState.initialActionPerformed,
    variantTreatmentBundleTypeReferenceId,
    choiceDispatch,
    handleSelectTreatment,
  ])

  const selectedTreatmentId = useMemo(() => {
    const findTreatmentById = (treatments?: CategorizedTreatmentsWithPartialTreatments[]) =>
      treatments
        ?.flatMap((category) => category.treatmentBundles)
        .find((treatment) => treatment.referenceId === treatmentBundleTypeReferenceId)

    const selectedTreatmentInUniqueTreatments = findTreatmentById(uniqueCategorizedTreatments)
    const selectedTreatmentInAllTreatments = findTreatmentById(categorizedTreatments)

    if (!clinicId || clinicId === selectedTreatmentInUniqueTreatments?.clinicReferenceId) {
      return selectedTreatmentInUniqueTreatments?.referenceId
    }

    return uniqueCategorizedTreatments
      ?.flatMap((category) => category.treatmentBundles)
      .find((treatment) => treatment.titleKey === selectedTreatmentInAllTreatments?.titleKey)?.referenceId
  }, [categorizedTreatments, clinicId, treatmentBundleTypeReferenceId, uniqueCategorizedTreatments])

  const isSuggestedTreatmentTest = useIsSuggestedTreatment()
  const categorizedTreatmentsToDisplay = isSuggestedTreatmentTest
    ? [getSuggestedTreatmentsCategory(categorizedTreatments), ...(uniqueCategorizedTreatments || [])]
    : uniqueCategorizedTreatments

  return (
    <TreatmentScreen
      selectedTreatmentId={selectedTreatmentId}
      categorizedTreatments={categorizedTreatmentsToDisplay}
      loading={isLoading}
      handleSelectTreatment={handleSelectTreatment}
      handleNextClicked={goToNextPage}
    />
  )
}
